/**
 * VersichererBenachrichtigungenPanel.tsx - P4-S7 step 24 (Fristüberwachung, visible).
 *
 * Shows every Versicherer-Benachrichtigung of a mandate: carrier, status, sent_at, response_due_at,
 * an overdue badge, and a «Manuell schliessen» action (with a Notiz) on an open row. The reminder loop
 * (P4 step 24): «Erinnert am … · Frist 2 bis …», «Klärung seit … · Aufgabe», «Jetzt erinnern»
 * (communication.send + mandate.eingangskontrolle) and «Eskalieren» (mandate.eingangskontrolle). The
 * list is a TanStack Query read, so a send from the dialog or a close here refreshes it.
 */

import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { OPEN_NOTIFICATION_STATUSES } from '@brokly/shared';
import {
  useSchliessenVersichererBenachrichtigung,
  useVersichererBenachrichtigungen,
  useVersichererBenachrichtigungReminder,
} from './versicherer-benachrichtigung-queries';
import { ProcessCloseCard } from './ProcessCloseCard';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { REGISTRATION_DECISION_KEY, REGISTRATION_SEND_KEYS } from './MandateRegistrationPanel';
import { RegistrationReminderLines } from './RegistrationReminderLines';
import { registrationErrorText } from './registration-error-text';

function fmtDate(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '-';
}

export function VersichererBenachrichtigungenPanel({
  mandateId,
  canClose = false,
  readOnly = false,
}: {
  mandateId: string;
  canClose?: boolean;
  /** P6: the customer is inaktiv (write-protected): the lines only. */
  readOnly?: boolean;
}) {
  const list = useVersichererBenachrichtigungen(mandateId);
  const close = useSchliessenVersichererBenachrichtigung(mandateId);
  const reminder = useVersichererBenachrichtigungReminder(mandateId);
  const { can } = useEffectivePermissions();
  // One permission map: «Jetzt erinnern» mails the insurer (both keys), «Eskalieren» mails nobody.
  const canRemind = REGISTRATION_SEND_KEYS.every((k) => can(k)) && !readOnly;
  const canEscalate = can(REGISTRATION_DECISION_KEY) && !readOnly;
  const [closingId, setClosingId] = useState<string | null>(null);
  const [notiz, setNotiz] = useState('');

  function onClose(notificationId: string) {
    close.mutate(
      { notificationId, notiz },
      {
        onSuccess: () => {
          setClosingId(null);
          setNotiz('');
        },
      },
    );
  }

  if (list.isError) {
    return (
      <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
        <AlertTriangle className="size-4" />
        Die Benachrichtigungen konnten nicht geladen werden.
      </p>
    );
  }
  if (list.data === undefined) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
        <Loader2 className="size-4 animate-spin" /> Lädt …
      </p>
    );
  }
  const rows = list.data;

  return (
    <section className="flex flex-col gap-2 rounded-lg border bg-card p-4" aria-label="Versicherer-Benachrichtigungen">
      {/* P4-S8 (step 27): the Prozessabschluss card (reads its own state from the persisted mandate id). */}
      <ProcessCloseCard mandateId={mandateId} />
      <h3 className="text-sm font-semibold">Versicherer-Benachrichtigungen</h3>
      {/* A failed close keeps the list: its error shows above it, and the list reloads to the current state. */}
      {close.isError && (
        <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertTriangle className="size-4" />
          Die Benachrichtigung konnte nicht geschlossen werden.
        </p>
      )}
      {reminder.isError && (
        <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertTriangle className="size-4" />
          {registrationErrorText(reminder.error)}
        </p>
      )}
      {rows.length === 0 && <p className="text-xs text-muted-foreground">Noch keine Benachrichtigung versendet.</p>}
      <ul className="flex flex-col gap-2">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-col gap-1 rounded-md border px-3 py-2 text-sm" data-testid="vbp-row">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-medium">{r.carrier_name ?? r.carrier_uid}</span>
              <span className="rounded bg-muted px-1.5 py-0.5 text-xs" data-testid="vbp-status">
                {r.status_label}
              </span>
              {r.overdue && (
                <span className="rounded bg-sem-decide-bg px-1.5 py-0.5 text-xs text-sem-decide" data-testid="vbp-overdue">
                  überfällig
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span>Gesendet: {fmtDate(r.sent_at)}</span>
              <span>Frist bis: {fmtDate(r.response_due_at)}</span>
            </div>
            <RegistrationReminderLines row={r} />
            {((canRemind && r.actions.erinnern) || (canEscalate && (r.actions.eskalieren || r.actions.versand_bestaetigen))) && (
              <div className="mt-1 flex flex-wrap gap-2">
                {canRemind && r.actions.erinnern && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={reminder.isPending}
                    onClick={() => reminder.mutate({ action: 'erinnern', notificationId: r.id })}
                    data-testid="vbp-erinnern"
                  >
                    Jetzt erinnern
                  </Button>
                )}
                {canEscalate && r.actions.versand_bestaetigen && (
                  <Button size="sm" variant="outline" disabled={reminder.isPending} onClick={() => reminder.mutate({ action: 'versand_bestaetigt', notificationId: r.id })} data-testid="vbp-versand-bestaetigt">
                    Versand bestätigt
                  </Button>
                )}
                {canEscalate && r.actions.nicht_versendet && (
                  <Button size="sm" variant="outline" disabled={reminder.isPending} onClick={() => reminder.mutate({ action: 'nicht_versendet', notificationId: r.id })} data-testid="vbp-nicht-versendet">
                    Nicht versendet
                  </Button>
                )}
                {canEscalate && r.actions.eskalieren && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={reminder.isPending}
                    onClick={() => reminder.mutate({ action: 'eskalieren', notificationId: r.id })}
                    data-testid="vbp-eskalieren"
                  >
                    Eskalieren
                  </Button>
                )}
              </div>
            )}
            {canClose && !readOnly && OPEN_NOTIFICATION_STATUSES.includes(r.status) && (
              <div className="mt-1">
                {closingId === r.id ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      className="min-w-48 flex-1 rounded border px-2 py-1 text-sm"
                      placeholder="Notiz"
                      value={notiz}
                      onChange={(e) => setNotiz(e.target.value)}
                      aria-label="Notiz"
                    />
                    <Button
                      size="sm"
                      disabled={close.isPending || notiz.trim() === ''}
                      onClick={() => onClose(r.id)}
                      data-testid="vbp-close-confirm"
                    >
                      Schliessen
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setClosingId(null)}>
                      Abbrechen
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setClosingId(r.id)} data-testid="vbp-close">
                    Manuell schliessen
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
